/**
 * Hook for mod actions (delete / timeout / ban / unban / chat-settings / automod).
 * Calls the /api/mod/* endpoints. All errors propagate via the returned promise
 * so callers can render inline status. Server returns 403 on missing auth/role
 * (auto-logout is NOT triggered — see api.ts).
 */

import { useCallback } from 'react';
import { apiFetch } from '../lib/api.js';

export interface ChatSettings {
  broadcaster_id: string;
  slow_mode: boolean;
  slow_mode_wait_time: number | null;
  follower_mode: boolean;
  follower_mode_duration: number | null;
  subscriber_mode: boolean;
  emote_mode: boolean;
  unique_chat_mode: boolean;
}

export interface ChatSettingsPatch {
  slow_mode?: boolean;
  slow_mode_wait_time?: number;
  follower_mode?: boolean;
  follower_mode_duration?: number;
  subscriber_mode?: boolean;
  emote_mode?: boolean;
  unique_chat_mode?: boolean;
}

export interface AutoModItem {
  id: string;
  message_id: string;
  target_user_id: string;
  target_login: string;
  target_display_name: string;
  text: string;
  category: string | null;
  level: string | null;
  created_at: string;
}

export function useModActions() {
  const deleteMessage = useCallback(
    async (messageId: string, source: 'twitch' | 'youtube' = 'twitch'): Promise<void> => {
      const qs = source === 'youtube' ? '?source=youtube' : '';
      await apiFetch(`/api/mod/message/${encodeURIComponent(messageId)}${qs}`, { method: 'DELETE' });
    },
    [],
  );

  const timeoutUser = useCallback(
    async (
      targetUserId: string,
      durationSeconds: number,
      reason?: string,
      source: 'twitch' | 'youtube' = 'twitch',
    ): Promise<void> => {
      await apiFetch('/api/mod/timeout', {
        method: 'POST',
        body: JSON.stringify({
          targetUserId,
          duration: durationSeconds,
          source,
          ...(reason ? { reason } : {}),
        }),
      });
    },
    [],
  );

  const banUser = useCallback(
    async (
      targetUserId: string,
      reason?: string,
      source: 'twitch' | 'youtube' = 'twitch',
    ): Promise<void> => {
      await apiFetch('/api/mod/ban', {
        method: 'POST',
        body: JSON.stringify({ targetUserId, source, ...(reason ? { reason } : {}) }),
      });
    },
    [],
  );

  const unbanUser = useCallback(async (targetUserId: string): Promise<void> => {
    await apiFetch(`/api/mod/ban/${encodeURIComponent(targetUserId)}`, { method: 'DELETE' });
  }, []);

  const getChatSettings = useCallback(async (): Promise<ChatSettings | null> => {
    const r = await apiFetch<{ settings: ChatSettings | null }>('/api/mod/chat-settings');
    return r.settings;
  }, []);

  const patchChatSettings = useCallback(
    async (patch: ChatSettingsPatch): Promise<ChatSettings | null> => {
      const r = await apiFetch<{ ok: boolean; settings: ChatSettings | null }>(
        '/api/mod/chat-settings',
        { method: 'PATCH', body: JSON.stringify(patch) },
      );
      return r.settings;
    },
    [],
  );

  const getAutoModQueue = useCallback(async (limit = 50): Promise<AutoModItem[]> => {
    const r = await apiFetch<{ items: AutoModItem[] }>(
      `/api/mod/automod/queue?limit=${limit}`,
    );
    return r.items;
  }, []);

  const approveAutoMod = useCallback(async (messageId: string): Promise<void> => {
    await apiFetch(`/api/mod/automod/approve/${encodeURIComponent(messageId)}`, {
      method: 'POST',
    });
  }, []);

  const denyAutoMod = useCallback(async (messageId: string): Promise<void> => {
    await apiFetch(`/api/mod/automod/deny/${encodeURIComponent(messageId)}`, {
      method: 'POST',
    });
  }, []);

  return {
    deleteMessage,
    timeoutUser,
    banUser,
    unbanUser,
    getChatSettings,
    patchChatSettings,
    getAutoModQueue,
    approveAutoMod,
    denyAutoMod,
  };
}
